import { useState, useCallback } from 'react'
import { Copy, Check } from 'lucide-react'

interface CodeBlockProps {
  content: string
  language?: string
  onCopy?: () => void
  showLineNumbers?: boolean
}

export default function CodeBlock({
  content,
  language,
  onCopy,
  showLineNumbers = true,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false)

  const lines = content.split('\n')

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      onCopy?.()
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }, [content, onCopy])

  return (
    <div className="ui-card overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-stroke bg-bg">
        <div className="flex items-center gap-2">
          {language && (
            <span className="text-xs font-medium text-faint uppercase tracking-wider">
              {language}
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="ui-btn ui-btn-ghost px-2.5 py-1 rounded-md text-xs"
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <>
              <Check size={14} className="text-accent" />
              <span className="text-accent">已复制</span>
            </>
          ) : (
            <>
              <Copy size={14} />
              <span>复制</span>
            </>
          )}
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="flex">
          {showLineNumbers && (
            <div className="select-none text-right text-faint bg-bg/60 border-r border-stroke whitespace-nowrap py-3 px-3 flex flex-col">
              {lines.map((_, i) => (
                <span key={i} className="text-xs leading-relaxed">
                  {i + 1}
                </span>
              ))}
            </div>
          )}
          <div className="flex-1 py-3 px-4 flex flex-col">
            {lines.map((line, i) => (
              <span key={i} className="text-sm text-muted leading-relaxed whitespace-pre">
                {line || '\u00A0'}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
